import { ItemType } from "~/utils/enums/common.enums";
import { RolesEnums } from "~/utils/enums/roles.enums";

/**
 * Whether a user holding `userRoles` is forbidden from a route that declares
 * `requiredRoles` (must hold at least one) and/or `excludeRoles` (must hold
 * none). SIMBEL_ADMIN bypasses every check.
 *
 * `excludeRoles` lets a guard mirror the NavBar's negative conditions — e.g. the
 * referent pages (my-catalog / my-sessions) are shown only when
 * `!isCompanyAdmin && (isTrainingPlanAdmin || isTrainingAdmin)`, so they declare
 * `roles: [TRAINING_PLAN_ADMIN, TRAINING_ADMIN]` + `excludeRoles: [COMPANY_ADMIN]`.
 * Keeping the guard and the menu in sync avoids "the tab is hidden but the page
 * is still reachable" situations.
 */
export const isRouteForbidden = (
  requiredRoles: RolesEnums[] | undefined,
  excludeRoles: RolesEnums[] | undefined,
  userRoles: RolesEnums[] | undefined,
): boolean => {
  if (userRoles?.includes(RolesEnums.SIMBEL_ADMIN)) return false;

  if (requiredRoles?.length && !userRoles?.some((role) => requiredRoles.includes(role))) return true;

  if (excludeRoles?.length && userRoles?.some((role) => excludeRoles.includes(role))) return true;

  return false;
};

/**
 * The 403 error shown when a route is forbidden — pass to `showError(...)`.
 * Centralized so the status code and message stay consistent across the
 * roles-guard middleware, the 409 handler, and the focus-revalidation plugin.
 *
 * `fatal: true` is required: on the client a createError is otherwise non-fatal,
 * so `showError()` would not render the full-screen error page and the user would
 * keep interacting with the forbidden page (the 403 would surface only as an
 * uncaught promise rejection).
 */
export const forbiddenError = () =>
  createError({ statusCode: 403, message: "Vous n'avez pas accès à cette page", fatal: true });

/** Where a trainer opening a /sessions link they can't see is sent instead, or `null` to let it through. */
export const trainerSessionRedirect = (
  to: { path: string; query: Record<string, unknown>; meta: Record<string, unknown> },
  userRoles: RolesEnums[] | undefined,
  isInternalTrainer: boolean,
  referentPageMeta: Record<string, unknown> | undefined,
): string | null => {
  if (to.path !== "/sessions") return null;
  if (!isInternalTrainer) return null;

  const sessionId = to.query.id;
  if (typeof sessionId !== "string" || !sessionId) return null;

  const forbidden = isRouteForbidden(
    to.meta.roles as RolesEnums[] | undefined,
    to.meta.excludeRoles as RolesEnums[] | undefined,
    userRoles,
  );
  if (!forbidden) return null;

  const referentPageOpen =
    !!referentPageMeta?.roles &&
    !isRouteForbidden(
      referentPageMeta.roles as RolesEnums[],
      referentPageMeta.excludeRoles as RolesEnums[] | undefined,
      userRoles,
    );
  if (referentPageOpen) {
    // /sessions is closed to referents, but /my-sessions gives them the full view, which beats the trainer view.
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(to.query)) {
      for (const item of Array.isArray(value) ? value : [value]) {
        if (typeof item === "string") query.append(key, item);
      }
    }
    if (!query.get("type")) query.set("type", ItemType.SESSION);
    return `/my-sessions?${query}`;
  }

  return `/trainer-activities/${sessionId}`;
};
